import { useEffect } from 'react'
import { Transaction, Transactions, useTransaction } from '@web/walter-lib/stream'
import { useSWRConfig } from 'swr'
import { restKeys } from '~api/swr/keys'

const DEFAULT_TRANSACTION_KEYS = [
    '#limit:30,key:"getTransactionsHistory",group_transactions:true,',
    '#limit:6,key:"getTransactionsHistory",group_transactions:true,',
]

export const useHistoryUpdate = () => {
    const { data: socketData } = useTransaction()
    const { cache, mutate } = useSWRConfig()

    useEffect(() => {
        if (socketData) {
            writeToCache(socketData, cache, mutate)
        }
    }, [socketData, cache, mutate])
}

function modifyExistingTransactions(cachedData: Transactions | undefined, data: Transaction): Transactions {
    const isFiatTransfer = Boolean(data.meta?.fiat_transfer)

    if (isFiatTransfer) {
        updateFiatTransferTransaction(cachedData, data)
    }

    const cachedTransactionId = cachedData?.find(tx => tx.native?.tx_id === data.native?.tx_id)?.native?.tx_id

    if (cachedTransactionId) {
        const filteredData = cachedData.filter(tx => tx.native?.tx_id !== cachedTransactionId)

        return [data, ...filteredData]
    }

    return [data, ...(cachedData ?? [])]
}

function writeToCache(
    data: Transaction,
    cache: ReturnType<typeof useSWRConfig>['cache'],
    mutate: ReturnType<typeof useSWRConfig>['mutate']
) {
    const transactionsKeys = [...cache.keys()].filter(
        key =>
            key.includes(restKeys.getTransactionsHistory) &&
            (key.includes(data?.type || '') || key.includes(data?.native?.currency || ''))
    )

    DEFAULT_TRANSACTION_KEYS.forEach(defaultKey => {
        mutate(defaultKey, (cachedData: Transactions | undefined) => {
            if (cachedData) {
                return modifyExistingTransactions(cachedData, data)
            }

            return cachedData
        })
    })

    if (!transactionsKeys.length) {
        return
    }

    transactionsKeys.forEach(key => {
        mutate(key, (cachedData: Transactions | undefined) => modifyExistingTransactions(cachedData, data))
    })
}

function updateFiatTransferTransaction(cachedData: Transactions | undefined, data: Transaction): Transactions {
    const cachedTransactionId = cachedData?.find(tx => tx?.operation_id === data.meta?.fiat_transfer?.id)?.operation_id

    if (cachedTransactionId) {
        const filteredData = cachedData.filter(tx => tx.operation_id !== cachedTransactionId)

        return [data, ...filteredData]
    }

    return [data, ...(cachedData ?? [])]
}
